// ReportEditor.jsx — v161.22.24
//
// Edit a draft report: add bug entries one at a time, attach screenshots
// and screen recordings, then submit the whole report at the end of the
// session. On submit, every entry becomes a dist_feedback row and a
// dist_fix_requests row is created — AgentMac picks it up via the same
// flow used by the legacy Feedback page.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bugReports } from '../lib/api.js';

const REPRODUCES_OPTIONS = [
  { value: 'always',          label: 'Always (every attempt)' },
  { value: 'intermittent_3_5', label: 'Intermittent (3 of 5)' },
  { value: 'intermittent_1_5', label: 'Rare (1 of 5)' },
  { value: 'once',            label: 'Saw it once' }
];

const SEVERITY_OPTIONS = [
  { value: 'critical',         label: 'Critical' },
  { value: 'high',             label: 'High' },
  { value: 'normal',           label: 'Normal' },
  { value: 'low',              label: 'Low' },
  { value: 'feature_request',  label: 'Feature request' }
];

const MAX_SCREENSHOT_MB = 4;
const MAX_RECORDING_MB = 50;

export default function ReportEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  async function refresh() {
    try {
      const r = await bugReports.get(id);
      setReport(r.report);
      setEntries(r.entries || []);
      // If the report has been submitted already, bounce to read-only detail.
      if (r.report.state !== 'draft') {
        navigate(`/reports/${id}`, { replace: true });
      }
    } catch (e) { setError(e.message); }
  }

  async function handleRemove(entryId) {
    if (!confirm('Remove this bug entry?')) return;
    try {
      await bugReports.removeEntry(id, entryId);
      setEntries(entries.filter(x => x.id !== entryId));
    } catch (e) { setError(e.message); }
  }

  async function handleSubmit() {
    if (entries.length === 0) {
      setError('Add at least one bug entry before submitting.');
      return;
    }
    if (!confirm(
      `Submit this report?\n\n${entries.length} bug${entries.length === 1 ? '' : 's'} ` +
      `will be sent to Claude for auto-fix. You won't be able to edit afterwards, ` +
      `but you can track progress on the report page.`
    )) return;
    setSubmitting(true);
    setError('');
    try {
      const r = await bugReports.submit(id);
      // v161.22.30: high-confidence "already fixed" match short-circuits
      // submission server-side — the report is now in 'likely_duplicate'
      // state with no fix request queued. Surface this clearly.
      if (r.state === 'likely_duplicate') {
        const top = (r.possibleAlreadyFixed || []).slice(0, 3);
        const list = top.map(m =>
          `• "${m.bugTitle}" — fixed in build ${m.fixedBuildLabel} on ${(m.fixedAt || '').slice(0, 10)}` +
          (m.reason ? ` (${m.reason})` : '')
        ).join('\n');
        alert(
          `This report looks like a bug we already fixed recently:\n\n${list}\n\n` +
          `Try installing the most recent build of this app first. ` +
          `If the bug is actually still happening, open the report and tap ` +
          `"Run auto-fix anyway" to queue a fresh fix.`
        );
        navigate(`/reports/${id}`, { replace: true });
        return;
      }
      // v161.22.26: dedup warning — soft, non-blocking.
      if (Array.isArray(r.possibleDuplicates) && r.possibleDuplicates.length > 0) {
        const dupList = r.possibleDuplicates
          .map(d => `• ${d.reportTitle} (${d.overlappingBugs} matching bug${d.overlappingBugs === 1 ? '' : 's'})`)
          .join('\n');
        alert(
          `Submitted. Note: this report looks similar to ${r.possibleDuplicates.length} ` +
          `other recent report${r.possibleDuplicates.length === 1 ? '' : 's'}:\n\n${dupList}\n\n` +
          `That's not a problem — Claude will still process this one — but you may want ` +
          `to check whether you've already reported these bugs.`
        );
      }
      // v161.22.30: lower-confidence already-fixed matches also surface here.
      if (Array.isArray(r.possibleAlreadyFixed) && r.possibleAlreadyFixed.length > 0) {
        const fixList = r.possibleAlreadyFixed
          .slice(0, 3)
          .map(m => `• "${m.bugTitle}" (fixed in ${m.fixedBuildLabel})`)
          .join('\n');
        alert(
          `Submitted. Heads up: some of your bugs look similar to ones we've already fixed:\n\n${fixList}\n\n` +
          `Auto-fix will run anyway, but you may want to verify against the most recent build first.`
        );
      }
      navigate(`/reports/${id}`, { replace: true });
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  }

  async function handleDeleteDraft() {
    if (!confirm('Delete this draft report and all its entries?')) return;
    try {
      await bugReports.remove(id);
      navigate('/reports', { replace: true });
    } catch (e) { setError(e.message); }
  }

  if (error && !report) return <div className="error">Error: {error}</div>;
  if (!report) return <div className="loading">Loading…</div>;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>{report.title || 'Untitled session report'}</h1>
          <p className="muted">
            {report.app_slug}
            {report.app_version && ` · v${report.app_version}`}
            {report.device_model && ` · ${report.device_model}`}
            {report.ios_version && ` · iOS ${report.ios_version}`}
          </p>
        </div>
        <span className="badge badge-info">Draft</span>
      </header>

      <ol className="entry-list">
        {entries.map((e, idx) => (
          <li key={e.id} className="entry-card">
            <div className="entry-header">
              <span className="entry-index">#{idx + 1}</span>
              <strong className="entry-title">{e.title}</strong>
              <button
                className="btn btn-icon"
                onClick={() => handleRemove(e.id)}
                aria-label="Remove entry"
              >×</button>
            </div>
            <div className="entry-body">
              {e.steps?.length > 0 && (
                <ol className="entry-steps">
                  {e.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              )}
              {e.observed && <p><strong>Observed:</strong> {e.observed}</p>}
              {e.expected && <p><strong>Expected:</strong> {e.expected}</p>}
              <p className="muted">
                Reproduces: {e.reproduces}
                {e.hasScreenshot && ' · 📷 screenshot'}
                {e.hasRecording && ' · 🎥 recording'}
              </p>
            </div>
          </li>
        ))}
      </ol>

      {showForm ? (
        <NewEntryForm
          onSaved={async (entry) => {
            setEntries([...entries, entry]);
            setShowForm(false);
          }}
          onCancel={() => setShowForm(false)}
          reportId={id}
          setError={setError}
        />
      ) : (
        <button
          className="btn btn-primary btn-block"
          onClick={() => setShowForm(true)}
        >
          + Add Bug Entry
        </button>
      )}

      {error && <div className="error">{error}</div>}

      <div className="actions actions-sticky">
        <button
          className="btn btn-success"
          onClick={handleSubmit}
          disabled={submitting || entries.length === 0}
        >
          {submitting
            ? 'Submitting…'
            : `Submit Report (${entries.length} ${entries.length === 1 ? 'bug' : 'bugs'})`}
        </button>
        <button
          className="btn btn-danger"
          onClick={handleDeleteDraft}
          disabled={submitting}
        >
          Delete Draft
        </button>
      </div>
    </div>
  );
}

// ── New entry form ──────────────────────────────────────────────────────

function NewEntryForm({ onSaved, onCancel, reportId, setError }) {
  const [title, setTitle] = useState('');
  const [steps, setSteps] = useState(['']);
  const [observed, setObserved] = useState('');
  const [expected, setExpected] = useState('');
  const [reproduces, setReproduces] = useState('always');
  const [severity, setSeverity] = useState('normal');
  const [notes, setNotes] = useState('');
  const [screenshotBase64, setScreenshotBase64] = useState(null);
  const [recordingBase64, setRecordingBase64] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const recordingInputRef = useRef(null);

  function updateStep(i, v) {
    const next = [...steps];
    next[i] = v;
    setSteps(next);
  }

  function addStep() {
    if (steps.length >= 30) return;
    setSteps([...steps, '']);
  }

  function removeStep(i) {
    if (steps.length <= 1) { setSteps(['']); return; }
    setSteps(steps.filter((_, idx) => idx !== i));
  }

  async function handleFile(file, setter, maxMB, kind) {
    if (!file) return;
    if (file.size > maxMB * 1024 * 1024) {
      setError(`${kind} too large — max ${maxMB} MB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      // strip data URL prefix to get raw base64
      const b64 = String(reader.result).replace(/^data:[^;]+;base64,/, '');
      setter(b64);
    };
    reader.readAsDataURL(file);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!title.trim()) { setError('Bug title required'); return; }
    setSaving(true);
    setError('');
    try {
      const entry = await bugReports.addEntry(reportId, {
        title: title.trim(),
        steps: steps.map(s => s.trim()).filter(Boolean),
        observed: observed.trim(),
        expected: expected.trim(),
        reproduces,
        notes: notes.trim(),
        severity,
        screenshotBase64,
        recordingBase64
      });
      onSaved(entry);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <form className="entry-form" onSubmit={handleSave}>
      <h3>Add Bug Entry</h3>

      <div className="field">
        <label>Title <span className="req">*</span></label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="One-line verb-phrase, e.g. 'Undo button has no effect after typing'"
          maxLength={200}
          required
        />
      </div>

      <div className="field">
        <label>Steps to reproduce</label>
        <ol className="steps-input">
          {steps.map((s, i) => (
            <li key={i} className="step-row">
              <input
                type="text"
                value={s}
                onChange={e => updateStep(i, e.target.value)}
                placeholder={`Step ${i + 1}`}
              />
              {steps.length > 1 && (
                <button type="button" className="btn btn-icon" onClick={() => removeStep(i)}>×</button>
              )}
            </li>
          ))}
        </ol>
        <button type="button" className="btn btn-text" onClick={addStep}>
          + Add step
        </button>
      </div>

      <div className="field">
        <label>Observed</label>
        <textarea
          value={observed}
          onChange={e => setObserved(e.target.value)}
          rows={2}
          placeholder="What actually happened. Be specific. Terse is fine."
        />
      </div>

      <div className="field">
        <label>Expected</label>
        <textarea
          value={expected}
          onChange={e => setExpected(e.target.value)}
          rows={2}
          placeholder="What should have happened."
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label>Reproduces</label>
          <select value={reproduces} onChange={e => setReproduces(e.target.value)}>
            {REPRODUCES_OPTIONS.map(o =>
              <option key={o.value} value={o.value}>{o.label}</option>
            )}
          </select>
        </div>
        <div className="field">
          <label>Severity</label>
          <select value={severity} onChange={e => setSeverity(e.target.value)}>
            {SEVERITY_OPTIONS.map(o =>
              <option key={o.value} value={o.value}>{o.label}</option>
            )}
          </select>
        </div>
      </div>

      <div className="field">
        <label>Screenshot (optional)</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={e => handleFile(e.target.files[0], setScreenshotBase64,
                                    MAX_SCREENSHOT_MB, 'Screenshot')}
        />
        {screenshotBase64 && <small className="muted">✓ Screenshot attached</small>}
      </div>

      <div className="field">
        <label>Screen recording (optional)</label>
        <input
          ref={recordingInputRef}
          type="file"
          accept="video/*"
          onChange={e => handleFile(e.target.files[0], setRecordingBase64,
                                    MAX_RECORDING_MB, 'Recording')}
        />
        <small className="muted">
          Record via iOS Control Center → screen record button. Max {MAX_RECORDING_MB} MB.
        </small>
        {recordingBase64 && <small className="muted">✓ Recording attached</small>}
      </div>

      <div className="field">
        <label>Notes (optional)</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          placeholder="Patterns, hypotheses, workarounds you tried."
        />
      </div>

      <div className="actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Entry'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}
